import ParseUtils from "../utils/parse-utils.ts";
import Coordinates from "./coordinates.ts";
import Location from "./location.ts";

export default class Restaurant {
  public readonly id: string;
  public readonly alias: string;
  public readonly name: string;
  public readonly imageUrl: string;
  public readonly url: string;
  public readonly reviewCount: number;
  public readonly categories: string[];
  public readonly rating: number;
  public readonly coordinates: Coordinates;
  public readonly price: string;
  public readonly location: Location;
  public readonly phone: string;
  public readonly displayPhone: string;
  public readonly distanceMeters: number;

  constructor(
    id: string,
    alias: string,
    name: string,
    imageUrl: string,
    url: string,
    reviewCount: number,
    categories: string[],
    rating: number,
    coordinates: Coordinates,
    price: string,
    location: Location,
    phone: string,
    displayPhone: string,
    distanceMeters: number,
  ) {
    this.id = id;
    this.alias = alias;
    this.name = name;
    this.imageUrl = imageUrl;
    this.url = url;
    this.reviewCount = reviewCount;
    this.categories = categories;
    this.rating = rating;
    this.coordinates = coordinates;
    this.price = price;
    this.location = location;
    this.phone = phone;
    this.displayPhone = displayPhone;
    this.distanceMeters = distanceMeters;
  }

  public static fromPayload(payload: Record<string, unknown>): Restaurant {
    const id: string = ParseUtils.parseString(payload, "id");
    const alias: string = ParseUtils.parseString(payload, "alias");
    const name: string = ParseUtils.parseString(payload, "name");
    const imageUrl: string = ParseUtils.parseString(payload, "image_url");
    const url: string = ParseUtils.parseString(payload, "url");
    const reviewCount: number = ParseUtils.parseNumber(payload, "review_count");
    const categories: string[] = ParseUtils.parseStringArray(payload, "categories");
    const rating: number = ParseUtils.parseNumber(payload, "rating");
    const coordinates: Coordinates = Coordinates.fromPayload(ParseUtils.parseObject(payload, "coordinates"));
    const price: string = ParseUtils.parseString(payload, "price");
    const location: Location = Location.fromPayload(ParseUtils.parseObject(payload, "location"));
    const phone: string = ParseUtils.parseString(payload, "phone");
    const displayPhone: string = ParseUtils.parseString(payload, "display_phone");
    const distanceMeters: number = ParseUtils.parseNumber(payload, "distance");

    return new Restaurant(
      id,
      alias,
      name,
      imageUrl,
      url,
      reviewCount,
      categories,
      rating,
      coordinates,
      price,
      location,
      phone,
      displayPhone,
      distanceMeters,
    );
  }

  public static parseArrayFromPayload(payload: Record<string, unknown>): Restaurant[] | null {
    // if field doesn't exist, return early
    if (payload["top_restaurants"] === undefined || payload["top_restaurants"] === null) {
      return null;
    }

    // at first, all we know is that this field is an array
    // we don't yet know what the array contains
    const unknownArray: unknown[] = ParseUtils.parseArray(payload, "top_restaurants");

    // convert the unknowns into Restaurants
    const topRestaurants: Restaurant[] = [];
    unknownArray.forEach((entry: unknown): void => {
      // convert entry into "fake payload" so that ParseUtils can be used to extract data
      const fakePayload: Record<string, unknown> = { "restaurant": entry };

      // convert "unknown" into "Restaurant"
      const restaurant: Restaurant = Restaurant.fromPayload(ParseUtils.parseObject(fakePayload, "restaurant"));

      // collect the parsed Restaurant
      topRestaurants.push(restaurant);
    });

    // if the array was empty, return null instead
    if (topRestaurants.length === 0) {
      return null;
    }

    // success
    return topRestaurants;
  }
}
